import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { USER_SETTINGS_KEY } from "core/local_storage_utils";
import { parseJsonString } from "core/str_utils";

import { addToNmckGroup } from "./services/addToNmckGroup";
import { createNmckCalc } from "./services/createNmckCalc";
import { createNmckGroup } from "./services/createNmckGroup";
import { createNmckItem } from "./services/createNmckItem";
import { createNmckReason } from "./services/createNmckReason";
import { deleteFromNmckGroup } from "./services/deleteFromNmckGroup";
import { deleteNmckCalc } from "./services/deleteNmckCalc";
import { deleteNmckGroup } from "./services/deleteNmckGroup";
import { deleteNmckItem } from "./services/deleteNmckItem";
import { deleteNmckReason } from "./services/deleteNmckReason";
import { editNmckCalc } from "./services/editNmckCalc";
import { editNmckEstimates } from "./services/editNmckEstimates";
import { editNmckGroup } from "./services/editNmckGroup";
import { editNmckItem } from "./services/editNmckItem";
import { editNmckReason } from "./services/editNmckReason";
import { editNmckReserv } from "./services/editNmckReserv";
import { exportXmlNmckItem } from "./services/exportXmlNmckItem";
import { fetchNmckBooks } from "./services/fetchNmckBooks";
import { fetchNmckEstimates } from "./services/fetchNmckEstimates";
import { fetchNmckItem } from "./services/fetchNmckItem";
import { printNmckItem } from "./services/printNmckItem";

import dayjs from "dayjs";

export const NmckDataKeyEnum = {
	TYPE: "type",
	RESERV: "reserv",
	VAT: "vat",
	SUFFIX: "suffix",
	PRICE_LEVEL_PERIOD: "priceLevelPeriod",
	CREATION_PERIOD: "creationPeriod",
	BUILDING_START_PERIOD: "buildingStartPeriod",
	BUILDING_END_PERIOD: "buildingEndPeriod",
	METHODIC: "methodic",
	COUNT_RET_MONTHS: "countRetMonths",
	RETENTION_PERIODS: "retentionPeriods",
	ESTIMATE_SOURCE: "estimateSource",
};

export const NmckEstimateSourceEnum = {
	PROJECT: "project",
	SUMMARY_ESTIMATE: "summary_estimate",
};

const currentMonth = dayjs().startOf("month");

const initialNmckData = {
	[NmckDataKeyEnum.TYPE]: null,
	[NmckDataKeyEnum.RESERV]: "",
	[NmckDataKeyEnum.VAT]: "20",
	[NmckDataKeyEnum.SUFFIX]: "",
	[NmckDataKeyEnum.PRICE_LEVEL_PERIOD]: null,
	[NmckDataKeyEnum.CREATION_PERIOD]: currentMonth,
	[NmckDataKeyEnum.BUILDING_START_PERIOD]: currentMonth,
	[NmckDataKeyEnum.BUILDING_END_PERIOD]: null,
	[NmckDataKeyEnum.METHODIC]: "base",
	[NmckDataKeyEnum.COUNT_RET_MONTHS]: true,
	[NmckDataKeyEnum.RETENTION_PERIODS]: [],
	[NmckDataKeyEnum.ESTIMATE_SOURCE]: NmckEstimateSourceEnum.PROJECT,
};

const initialState = () => {
	const userSettings = parseJsonString(localStorage.getItem(USER_SETTINGS_KEY));

	return {
		creationData: initialNmckData,
		editingData: initialNmckData,
		creationErrors: {},
		editingErrors: {},
		fetchStatus: "",
		fetchStatusText: undefined,
		calcTableData: [],
		estmTableData: [],
		baseData: null,
		reasonsData: [],
		inflationData: {},
		nmckList: [],
		calculationWidth: userSettings?.nmck?.calculationWidth ?? 1200,
		calcWidthOnTreeTab: userSettings?.nmck?.calcWidthOnTreeTab ?? 500,
		periods: [],
		types: [],
		reasonTypes: [],
		workTypes: [],
		booksFetchStatus: "",
		initEditingData: true,
	};
};

export const nmckSlice = createSlice({
	name: "nmck",
	initialState,
	reducers: {
		setCreationData: (state, { payload }) => {
			state.creationData = payload;
		},
		setEditingData: (state, { payload }) => {
			state.editingData = payload;
		},
		setFetchStatus: (state, { payload }) => {
			state.fetchStatus = payload;
		},
		setFetchStatusText: (state, { payload }) => {
			state.fetchStatusText = payload;
		},
		setCalcTableData: (state, { payload }) => {
			state.calcTableData = payload;
		},
		setEstmTableData: (state, { payload }) => {
			state.estmTableData = payload;
		},
		setBaseData: (state, { payload }) => {
			state.baseData = payload;
		},
		setReasonsData: (state, { payload }) => {
			state.reasonsData = payload;
		},
		setNmckList: (state, { payload }) => {
			state.nmckList = payload;
		},
		setCalculationWidth: (state, { payload }) => {
			state.calculationWidth = payload;
		},
		setCalcWidthOnTreeTab: (state, { payload }) => {
			state.calcWidthOnTreeTab = payload;
		},
		setPeriods: (state, { payload }) => {
			state.periods = payload;
		},
		setTypes: (state, { payload }) => {
			state.types = payload;
		},
		setReasonTypes: (state, { payload }) => {
			state.reasonTypes = payload;
		},
		setWorkTypes: (state, { payload }) => {
			state.workTypes = payload;
		},
		setBooksFetchStatus: (state, { payload }) => {
			state.booksFetchStatus = payload;
		},
		setInitEditingData: (state, { payload }) => {
			state.initEditingData = payload;
		},
		setCreationErrors: (state, { payload }) => {
			state.creationErrors = payload;
		},
		setEditingErrors: (state, { payload }) => {
			state.editingErrors = payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchNmckItem.fulfilled, (state, { payload }) => {
			state.calcTableData = payload.calc_data;
			state.baseData = payload.nmck_base_data;
			state.reasonsData = payload.reasons_data;
			state.inflationData = payload.inflation_data;
			state.nmckList = payload.nmck_list;
			state.initEditingData = true;
		});
		builder.addCase(fetchNmckBooks.pending, (state) => {
			state.booksFetchStatus = "loading";
		});
		builder.addCase(fetchNmckBooks.fulfilled, (state, { payload }) => {
			state.periods = payload.periods;
			state.types = payload.types;
			state.reasonTypes = payload.reason_types;
			state.workTypes = payload.work_types;
			state.booksFetchStatus = "success";
		});
		builder.addCase(fetchNmckBooks.rejected, (state) => {
			state.booksFetchStatus = "failed";
		});
		builder.addCase(editNmckItem.fulfilled, (state, { payload }) => {
			state.calcTableData = payload.calc_data;
			state.baseData = payload.nmck_base_data;
			state.reasonsData = payload.reasons_data;
			state.inflationData = payload.inflation_data;
			state.nmckList = payload.nmck_list;
		});
		builder.addCase(editNmckReason.fulfilled, (state, { payload }) => {
			state.reasonsData = payload;
			state.fetchStatus = "successAlert";
			state.fetchStatusText = "Основание успешно обновлено";
		});
		builder.addCase(editNmckReason.rejected, (state) => {
			state.fetchStatus = "failed";
			state.fetchStatusText = "Ошибка обновления основания";
		});
		builder.addCase(createNmckReason.fulfilled, (state, { payload }) => {
			state.reasonsData = payload;
			state.fetchStatus = "successAlert";
			state.fetchStatusText = "Основание успешно создано";
		});
		builder.addCase(createNmckReason.rejected, (state) => {
			state.fetchStatus = "failed";
			state.fetchStatusText = "Ошибка создания основания";
		});
		builder.addCase(deleteNmckReason.fulfilled, (state, { payload }) => {
			state.reasonsData = payload;
			state.fetchStatus = "successAlert";
			state.fetchStatusText = "Основание успешно удалено";
		});
		builder.addCase(deleteNmckReason.rejected, (state) => {
			state.fetchStatus = "failed";
			state.fetchStatusText = "Ошибка удаления основания";
		});
		builder.addCase(editNmckCalc.fulfilled, (state, { payload }) => {
			state.calcTableData = payload;
			state.fetchStatus = "successAlert";
			state.fetchStatusText = "Позиция успешно обновлена";
		});
		builder.addCase(editNmckCalc.rejected, (state) => {
			state.fetchStatus = "failed";
			state.fetchStatusText = "Ошибка обновления позиции";
		});
		builder.addCase(createNmckCalc.fulfilled, (state, { payload }) => {
			state.calcTableData = payload;
			state.fetchStatus = "successAlert";
			state.fetchStatusText = "Позиция успешно создана";
		});
		builder.addCase(createNmckCalc.rejected, (state) => {
			state.fetchStatus = "failed";
			state.fetchStatusText = "Ошибка создания позиции";
		});
		builder.addCase(deleteNmckCalc.fulfilled, (state, { payload }) => {
			state.calcTableData = payload.calc_data;
			state.estmTableData = payload.tree;
			state.fetchStatus = "successAlert";
			state.fetchStatusText = "Позиция успешно удалена";
		});
		builder.addCase(deleteNmckCalc.rejected, (state) => {
			state.fetchStatus = "failed";
			state.fetchStatusText = "Ошибка удаления позиции";
		});
		builder.addCase(fetchNmckEstimates.fulfilled, (state, { payload }) => {
			state.estmTableData = payload;
		});
		builder.addCase(editNmckEstimates.fulfilled, (state, { payload }) => {
			state.calcTableData = payload.nmck.calc_data;
			state.baseData = payload.nmck.nmck_base_data;
			state.reasonsData = payload.nmck.reasons_data;
			state.inflationData = payload.nmck.inflation_data;
			state.nmckList = payload.nmck.nmck_list;
			state.estmTableData = payload.tree;
			state.fetchStatus = "successAlert";
			state.fetchStatusText = "Состав расчета успешно изменен";
		});
		builder.addCase(editNmckEstimates.rejected, (state) => {
			state.fetchStatus = "failed";
			state.fetchStatusText = "Ошибка изменения состава расчета";
		});
		builder.addCase(deleteNmckItem.fulfilled, (state, { payload }) => {
			state.nmckList = payload;
		});
		builder.addCase(editNmckReserv.fulfilled, (state, { payload }) => {
			state.calcTableData = payload.calc_data;
			state.baseData = payload.nmck_base_data;
			state.reasonsData = payload.reasons_data;
			state.inflationData = payload.inflation_data;
			state.nmckList = payload.nmck_list;
		});
		builder.addCase(editNmckGroup.fulfilled, (state, { payload }) => {
			state.nmckList = payload.nmck_list;
		});
		builder.addCase(createNmckGroup.fulfilled, (state, { payload }) => {
			state.nmckList = payload.nmck_list;
		});
		builder.addCase(deleteNmckGroup.fulfilled, (state, { payload }) => {
			state.nmckList = payload.nmck_list;
		});
		builder.addCase(addToNmckGroup.fulfilled, (state, { payload }) => {
			state.nmckList = payload.nmck_list;
			state.estmTableData = payload.tree;
		});
		builder.addCase(addToNmckGroup.rejected, (state, { payload }) => {
			if (payload.response?.status === 409) {
				state.fetchStatusText = payload.response.data.text[0];
			}
			state.fetchStatus = "failed";
		});
		builder.addCase(deleteFromNmckGroup.fulfilled, (state, { payload }) => {
			state.nmckList = payload.nmck_list;
			state.estmTableData = payload.tree;
		});
		builder.addCase(createNmckItem.rejected, (state, { payload }) => {
			if (payload.response?.status === 409) {
				state.fetchStatusText = payload.response.data;
			}
			state.fetchStatus = "failed";
		});
		builder.addMatcher(
			isAnyOf(
				fetchNmckItem.fulfilled,
				editNmckItem.fulfilled,
				fetchNmckEstimates.fulfilled,
				printNmckItem.fulfilled,
				deleteNmckItem.fulfilled,
				editNmckReserv.fulfilled,
				editNmckGroup.fulfilled,
				createNmckGroup.fulfilled,
				deleteNmckGroup.fulfilled,
				addToNmckGroup.fulfilled,
				deleteFromNmckGroup.fulfilled,
				exportXmlNmckItem.fulfilled
			),
			(state) => {
				state.fetchStatus = "success";
			}
		);
		builder.addMatcher(
			isAnyOf(
				fetchNmckItem.pending,
				editNmckItem.pending,
				fetchNmckEstimates.pending,
				editNmckReason.pending,
				createNmckReason.pending,
				deleteNmckReason.pending,
				editNmckCalc.pending,
				createNmckCalc.pending,
				deleteNmckCalc.pending,
				editNmckEstimates.pending,
				printNmckItem.pending,
				deleteNmckItem.pending,
				editNmckReserv.pending,
				editNmckGroup.pending,
				createNmckGroup.pending,
				deleteNmckGroup.pending,
				addToNmckGroup.pending,
				deleteFromNmckGroup.pending,
				exportXmlNmckItem.pending
			),
			(state) => {
				state.fetchStatus = "loading";
			}
		);
		builder.addMatcher(
			isAnyOf(
				fetchNmckItem.rejected,
				editNmckItem.rejected,
				fetchNmckEstimates.rejected,
				printNmckItem.rejected,
				deleteNmckItem.rejected,
				editNmckReserv.rejected,
				editNmckGroup.rejected,
				createNmckGroup.rejected,
				deleteNmckGroup.rejected,
				deleteFromNmckGroup.rejected,
				exportXmlNmckItem.rejected
			),
			(state, action) => {
				state.fetchStatus = "failed";
				const errorText = action?.payload?.response?.data?.text;
				if (errorText) {
					state.fetchStatusText = String(errorText).slice(0, 700);
				}
			}
		);
	},
});

export const { actions: nmckActions, reducer: nmckReducer } = nmckSlice;
